/*
 * MusicCore — музыкальный движок курсов (гитара, вокал, фортепиано).
 *
 * Не новый детектор и не новая теория: всё, что «слышит», взято у тулов,
 * где это уже замерено, — здесь только склейка под шаг урока:
 *   • высота тона — GuitarTunerCore.Detector/Tracker (NSDF через DspFft,
 *     замер на EGFxSet/NSynth, guitar-tuner-core.js) или PitchDetect (голос);
 *   • хрома — __chromaStream (порт librosa из key-detector.js);
 *   • название аккорда — __atnChords.nameChroma (шаблоны живой полосы
 *     /audio-to-notes); поверх него — сверка с ЦЕЛЕВЫМ аккордом урока;
 *   • диаграммы — ChordDiagram (chord-finder/diagram.js);
 *   • «когда человек это услышал» — AudibleClock.
 * Своё здесь: захват с часами графа (music-capture-worklet.js), детектор
 * ударов для ритма, метроном с упреждением, синтез щипка (Карплус — Стронг)
 * и правила зачёта шага. Числа порогов — из замера, docs/why/guitar-course.md.
 *
 * Анализ — чистые функции над Float32Array (MusicCore.analysis.*): живой
 * микрофон и стенд tests/js/course-music-core.test.mjs гоняют один и тот же
 * код. Контракт для курсов — docs/courses.md, «Музыкальный движок курсов».
 */
(function (root) {
    'use strict';

    var MC = {};
    var CFG = (root && root.__MUSIC_CORE) || {};
    var MT = CFG.T || {};

    function mt(k, rep) {
        var s = MT[k] != null ? MT[k] : k;
        if (rep) s = String(s).replace(/:([a-z_]+)/g, function (m, x) { return rep[x] != null ? rep[x] : m; });
        return s;
    }
    MC.t = mt;

    /* ── ноты ───────────────────────────────────────────────────────── */

    var SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    var FLAT_ROOTS = { 'Db': 1, 'Eb': 3, 'Gb': 6, 'Ab': 8, 'Bb': 10 };
    function midiToFreq(m) { return 440 * Math.pow(2, (m - 69) / 12); }
    function freqToMidi(f) { return 69 + 12 * Math.log2(f / 440); }
    function noteName(midi) {
        var n = Math.round(midi);
        return SHARPS[((n % 12) + 12) % 12] + (Math.floor(n / 12) - 1);
    }
    function parseNote(s) {
        if (typeof s === 'number') return s;
        var m = /^([A-Ga-g])([#b]?)(-?\d)$/.exec(String(s).trim());
        if (!m) return null;
        var v = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[m[1].toUpperCase()] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
        return v + (parseInt(m[3], 10) + 1) * 12;
    }
    MC.midiToFreq = midiToFreq;
    MC.freqToMidi = freqToMidi;
    MC.noteName = noteName;
    MC.parseNote = parseNote;

    /* Строй гитары: от 6-й (самой толстой) к 1-й. Номер струны = 6 − индекс. */
    MC.TUNING = [40, 45, 50, 55, 59, 64];
    MC.stringMidi = function (num) { return MC.TUNING[6 - num]; };

    /* ── аккорды ────────────────────────────────────────────────────── */

    var QUALITY = {
        '': { id: 'maj', iv: [0, 4, 7] },
        'm': { id: 'min', iv: [0, 3, 7] },
        '7': { id: '7', iv: [0, 4, 7, 10] },
        'm7': { id: 'min7', iv: [0, 3, 7, 10] },
        'maj7': { id: 'maj7', iv: [0, 4, 7, 11] },
        'sus2': { id: 'sus2', iv: [0, 2, 7] },
        'sus4': { id: 'sus4', iv: [0, 5, 7] },
        '5': { id: '5', iv: [0, 7] },
        'add9': { id: 'add9', iv: [0, 2, 4, 7] }
    };
    /* Аппликатуры, какими их учат начинающих: лады от 6-й струны к 1-й
       (-1 — глушится), пальцы 1..4 (0 — открытая), баррэ {fret, from, to}
       в индексах струн 0..5. Это не поиск по грифу (ChordFingering), а
       «учебная» форма: у начинающего одна аппликатура на аккорд. */
    MC.SHAPES = {
        'C':     { frets: [-1, 3, 2, 0, 1, 0], fingers: [0, 3, 2, 0, 1, 0] },
        'Cmaj7': { frets: [-1, 3, 2, 0, 0, 0], fingers: [0, 3, 2, 0, 0, 0] },
        'C7':    { frets: [-1, 3, 2, 3, 1, 0], fingers: [0, 3, 2, 4, 1, 0] },
        'Cadd9': { frets: [-1, 3, 2, 0, 3, 0], fingers: [0, 2, 1, 0, 3, 0] },
        'D':     { frets: [-1, -1, 0, 2, 3, 2], fingers: [0, 0, 0, 1, 3, 2] },
        'Dm':    { frets: [-1, -1, 0, 2, 3, 1], fingers: [0, 0, 0, 2, 3, 1] },
        'D7':    { frets: [-1, -1, 0, 2, 1, 2], fingers: [0, 0, 0, 2, 1, 3] },
        'Dsus2': { frets: [-1, -1, 0, 2, 3, 0], fingers: [0, 0, 0, 1, 3, 0] },
        'Dsus4': { frets: [-1, -1, 0, 2, 3, 3], fingers: [0, 0, 0, 1, 2, 3] },
        'E':     { frets: [0, 2, 2, 1, 0, 0], fingers: [0, 2, 3, 1, 0, 0] },
        'Em':    { frets: [0, 2, 2, 0, 0, 0], fingers: [0, 2, 3, 0, 0, 0] },
        'E7':    { frets: [0, 2, 0, 1, 0, 0], fingers: [0, 2, 0, 1, 0, 0] },
        'Em7':   { frets: [0, 2, 0, 0, 0, 0], fingers: [0, 2, 0, 0, 0, 0] },
        'F':     { frets: [1, 3, 3, 2, 1, 1], fingers: [1, 3, 4, 2, 1, 1], barre: { fret: 1, from: 0, to: 5 } },
        'Fmaj7': { frets: [-1, -1, 3, 2, 1, 0], fingers: [0, 0, 3, 2, 1, 0] },
        'Fs':    { frets: [-1, -1, 3, 2, 1, 1], fingers: [0, 0, 3, 2, 1, 1], barre: { fret: 1, from: 4, to: 5 } },
        'G':     { frets: [3, 2, 0, 0, 0, 3], fingers: [2, 1, 0, 0, 0, 3] },
        'G7':    { frets: [3, 2, 0, 0, 0, 1], fingers: [3, 2, 0, 0, 0, 1] },
        'A':     { frets: [-1, 0, 2, 2, 2, 0], fingers: [0, 0, 1, 2, 3, 0] },
        'Am':    { frets: [-1, 0, 2, 2, 1, 0], fingers: [0, 0, 2, 3, 1, 0] },
        'A7':    { frets: [-1, 0, 2, 0, 2, 0], fingers: [0, 0, 2, 0, 3, 0] },
        'Am7':   { frets: [-1, 0, 2, 0, 1, 0], fingers: [0, 0, 2, 0, 1, 0] },
        'Asus2': { frets: [-1, 0, 2, 2, 0, 0], fingers: [0, 0, 1, 2, 0, 0] },
        'Asus4': { frets: [-1, 0, 2, 2, 3, 0], fingers: [0, 0, 1, 2, 3, 0] },
        'B7':    { frets: [-1, 2, 1, 2, 0, 2], fingers: [0, 2, 1, 3, 0, 4] },
        'Bm':    { frets: [-1, 2, 4, 4, 3, 2], fingers: [0, 1, 3, 4, 2, 1], barre: { fret: 2, from: 1, to: 5 } },
        'Bm7':   { frets: [-1, 2, 0, 2, 0, 2], fingers: [0, 1, 0, 2, 0, 3] },
        'E5':    { frets: [0, 2, -1, -1, -1, -1], fingers: [0, 1, 0, 0, 0, 0] },
        'A5':    { frets: [-1, 0, 2, -1, -1, -1], fingers: [0, 0, 1, 0, 0, 0] },
        'G5':    { frets: [3, 5, -1, -1, -1, -1], fingers: [1, 3, 0, 0, 0, 0] },
        'C5':    { frets: [-1, 3, 5, -1, -1, -1], fingers: [0, 1, 3, 0, 0, 0] },
        'D5':    { frets: [-1, 5, 7, -1, -1, -1], fingers: [0, 1, 3, 0, 0, 0] },
        'F5':    { frets: [1, 3, -1, -1, -1, -1], fingers: [1, 3, 0, 0, 0, 0] }
    };

    /* «Am», «F#m», «Bb7», «Fs» (лёгкий F) → {name, root, quality, pcs, iv, shape, strings}. */
    function chord(name) {
        var label = String(name);
        var key = label === 'Fs' ? 'F' : label;
        var m = /^([A-G])(#|b)?(maj7|m7|m|7|sus2|sus4|5|add9)?$/.exec(key);
        if (!m) return null;
        var rootName = m[1] + (m[2] || '');
        var root = FLAT_ROOTS[rootName] != null ? FLAT_ROOTS[rootName] : SHARPS.indexOf(rootName);
        if (root < 0) return null;
        var q = QUALITY[m[3] || ''];
        var pcs = q.iv.map(function (i) { return (root + i) % 12; });
        var shape = MC.SHAPES[label] || null;
        var strings = null;
        if (shape) {
            strings = shape.frets.map(function (f, i) { return f < 0 ? null : MC.TUNING[i] + f; });
        }
        return { name: label === 'Fs' ? 'F' : label, key: label, root: root, quality: q.id, iv: q.iv, pcs: pcs, shape: shape, strings: strings };
    }
    MC.chord = chord;

    /* ── синтез щипка (Карплус — Стронг) ────────────────────────────── */

    /* Струна: шумовой импульс через линию задержки с усреднением (затухание
       высоких — как у настоящей струны) и дробной задержкой (без неё высокие
       ноты уходят на 5–10 ¢). Тембр — позиция щипка (гребёнка) и мягкий
       «корпус» (однополюсный НЧ). Чистая функция: её же слушает стенд. */
    function renderPluck(midi, sr, opts) {
        opts = opts || {};
        var dur = opts.dur || 2.2;
        var f = midiToFreq(midi) * Math.pow(2, (opts.detuneCents || 0) / 1200);
        var n = Math.max(1, Math.floor(sr * dur));
        var out = new Float32Array(n);
        var P = sr / f - 0.5;                         // усреднение в петле — ещё ½ отсчёта
        var D = Math.floor(P), frac = P - D, L = D + 2;
        var buf = new Float32Array(L);
        var seed = opts.seed != null ? opts.seed : (midi * 7919) % 2147483647 || 1;
        function rnd() { seed = (seed * 16807) % 2147483647; return seed / 2147483647 * 2 - 1; }
        /* Возбуждение: шум через два однополюсных НЧ с частотой среза ~6–8
           гармоник (у настоящей струны верх быстро гаснет, у голого шума
           основной тон тонул на 10 дБ под 8-й гармоникой). */
        var fc = Math.min(5000, f * (opts.bright != null ? 4 + 8 * opts.bright : 8));
        var a = 1 - Math.exp(-2 * Math.PI * fc / sr), l1 = 0, l2 = 0, i;
        var ex = new Float32Array(L);
        for (i = 0; i < 4 * L; i++) { l1 += a * (rnd() - l1); l2 += a * (l1 - l2); if (i >= 3 * L) ex[i - 3 * L] = l2; }
        var pos = Math.max(1, Math.round(D * (opts.pickPos || 0.16)));
        var mean = 0, peak = 0;
        for (i = 0; i < L; i++) { buf[i] = ex[i] - 0.5 * ex[(i + pos) % L]; mean += buf[i]; }
        mean /= L;
        for (i = 0; i < L; i++) { buf[i] -= mean; peak = Math.max(peak, Math.abs(buf[i])); }
        if (peak > 0) for (i = 0; i < L; i++) buf[i] /= peak;
        var t60 = opts.sustain || (midi < 52 ? 3.4 : midi < 60 ? 2.8 : 2.2);
        var decay = Math.pow(0.001, 1 / (sr * t60) * D);   // за один оборот петли
        decay = Math.pow(decay, 1 / D);
        var w = 0, yPrev = 0, body = 0;
        var gain = opts.gain != null ? opts.gain : 0.35;
        var damp = opts.mute ? Math.floor(sr * 0.05) : -1;
        for (var t = 0; t < n; t++) {
            var r1 = (w - D + L) % L, r2 = (w - D - 1 + L) % L;
            var y = (1 - frac) * buf[r1] + frac * buf[r2];
            var nv = 0.5 * (y + yPrev) * decay;
            yPrev = y;
            if (damp >= 0 && t > damp) nv *= 0.55;      // заглушённая струна — короткий «тук» (раз за оборот петли)
            buf[w] = nv;
            w = (w + 1) % L;
            body += 0.3 * (y - body);
            var env = t < 48 ? t / 48 : 1;
            out[t] = (0.75 * y + 0.25 * body) * gain * env;
        }
        var fade = Math.min(n, Math.floor(sr * 0.08));     // без обрыва в конце (обрыв — щелчок)
        for (t = 0; t < fade; t++) out[n - 1 - t] *= t / fade;
        return out;
    }

    /* Аккорд боем: струны с разбросом spread (вниз — от толстой). midis — по
       струнам 6→1, null — не звучит. Возвращает Float32Array. */
    function renderStrum(midis, sr, opts) {
        opts = opts || {};
        var spread = opts.spread != null ? opts.spread : 0.014;
        var dur = opts.dur || 2.2;
        var list = midis.map(function (m, i) { return { m: m, i: i }; }).filter(function (x) { return x.m != null; });
        if (opts.dir === 'up') list.reverse();
        var out = new Float32Array(Math.floor(sr * (dur + spread * 6)));
        list.forEach(function (x, k) {
            var p = renderPluck(x.m, sr, {
                dur: dur, seed: (x.m * 131 + k * 17 + (opts.seed || 0)) % 2147483647 || 1,
                detuneCents: opts.detune ? opts.detune[x.i] || 0 : 0,
                gain: (opts.gain || 0.22) * (opts.levels ? (opts.levels[x.i] != null ? opts.levels[x.i] : 1) : 1),
                bright: opts.dir === 'up' ? 0.65 : 0.5,
                mute: opts.muted && opts.muted[x.i]
            });
            var off = Math.floor(sr * spread * k);
            for (var j = 0; j < p.length && off + j < out.length; j++) out[off + j] += p[j];
        });
        return out;
    }
    MC.synth = { renderPluck: renderPluck, renderStrum: renderStrum };

    var cache = {};
    function bufferOf(ctx, key, make) {
        var k = ctx.sampleRate + ':' + key;
        if (!cache[k]) {
            var data = make(ctx.sampleRate);
            var b = ctx.createBuffer(1, data.length, ctx.sampleRate);
            b.getChannelData(0).set(data);
            cache[k] = b;
        }
        return cache[k];
    }
    function playBuffer(ctx, buf, when, gain) {
        var src = ctx.createBufferSource();
        src.buffer = buf;
        var g = ctx.createGain();
        g.gain.value = gain == null ? 1 : gain;
        src.connect(g);
        g.connect(ctx.destination);
        src.start(when);
        return src;
    }
    /* Сыграть ноты: o.mode 'chord' — боем (o.dir 'down'|'up'), иначе мелодией
       с темпом o.bpm. notes — имена («E2»), MIDI или имя аккорда («Am»). */
    MC.play = function (ctx, notes, o) {
        o = o || {};
        var t0 = (o.when || ctx.currentTime) + 0.05;
        if (typeof notes === 'string' && chord(notes)) {
            var c = chord(notes);
            notes = c.strings || c.pcs.map(function (pc) { return 48 + pc; });
            o.mode = 'chord';
        }
        if (Array.isArray(notes) && notes.length && notes.every(function (x) { return typeof x === 'string' && chord(x); })) {
            var spb = 60 / (o.bpm || 80), per = o.beatsPer || 2, chain = Promise.resolve();
            notes.forEach(function (n, i) {
                var c = chord(n), at = t0 + i * spb * per;
                var b = bufferOf(ctx, 'strum:' + c.key, function (sr) { return renderStrum(c.strings || c.pcs.map(function (pc) { return 48 + pc; }), sr, { dur: spb * per + 0.6 }); });
                playBuffer(ctx, b, at, o.gain);
            });
            return new Promise(function (r) { setTimeout(r, (notes.length * spb * per + 0.8) * 1000); });
        }
        var list = (Array.isArray(notes) ? notes : [notes]).map(function (x) { return x == null ? null : parseNote(x); });
        if (o.mode === 'chord') {
            var key = 'strum:' + list.join(',') + ':' + (o.dir || 'down');
            var buf = bufferOf(ctx, key, function (sr) { return renderStrum(list, sr, { dir: o.dir, dur: o.dur || 2.4 }); });
            playBuffer(ctx, buf, t0, o.gain);
            return new Promise(function (r) { setTimeout(r, (buf.duration + 0.05) * 1000); });
        }
        var step = 60 / (o.bpm || 90);
        var end = t0;
        list.forEach(function (m, i) {
            if (m == null) return;
            var buf = bufferOf(ctx, 'pluck:' + m, function (sr) { return renderPluck(m, sr, { dur: 2 }); });
            playBuffer(ctx, buf, t0 + i * step, o.gain);
            end = t0 + i * step + Math.min(buf.duration, step * 1.5);
        });
        return new Promise(function (r) { setTimeout(r, Math.max(0, (end - ctx.currentTime) * 1000)); });
    };

    /* ── анализ: чистые функции ─────────────────────────────────────── */

    var FFT_N = 4096;
    var hannCache = {};
    function hann(n) {
        if (!hannCache[n]) {
            var w = new Float64Array(n);
            for (var i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (n - 1));
            hannCache[n] = w;
        }
        return hannCache[n];
    }
    function fftLib() { return root.DspFft || null; }

    /*
     * Своя модель судьи (ChordNet, public/js/course/steps/chord-net.js): на
     * настоящей гитаре шаблоны хромы путали мажор и минор (терция засчитана в
     * 21–34 % — docs/why/guitar-chord-model.md). Веса грузятся лениво по
     * CFG.chordNet; пока их нет или загрузка не удалась — судят шаблоны, как
     * раньше. Признаки окна едут «прицепом» к массиву хромы (pooled.net), поэтому
     * вызовы judgeChord(pooled, target, bass) у шагов не меняются. Заглушённая
     * струна у сети — по-прежнему подсказка (weakStrings), а не отказ.
     */
    var NET = { W: null, loading: null, told: {} };
    function netReady() { return !!(NET.W && root.ChordNet); }
    function tellJudge(path) {
        if (NET.told[path]) return;
        NET.told[path] = 1;
        try { if (root.__ae) root.__ae('course_chord_judge', { path: path }); } catch (_) {}
    }
    function ensureNet() {
        if (NET.W || NET.loading || !CFG.chordNet || !root.ChordNet || typeof fetch !== 'function') return;
        NET.loading = fetch(CFG.chordNet, { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (w) { if (w && w.w1 && w.T) NET.W = w; else tellJudge('net_fail'); }, function () { tellJudge('net_fail'); });
    }
    function withNet(pooled, netFrames) {
        if (pooled && netReady()) {
            var nf = root.ChordNet.pool(netFrames.filter(function (f) { return !!f; }));
            if (nf) pooled.net = nf;
        }
        return pooled;
    }

    /* Хрома одного окна (последние FFT_N отсчётов frame). null — тишина. */
    function ChromaMeter(sr) {
        this.sr = sr;
        this.bank = root.__chromaStream.bank(sr, FFT_N, 0);
        this.re = new Float64Array(FFT_N);
        this.im = new Float64Array(FFT_N);
        this.mag = new Float64Array((FFT_N >> 1) + 1);
        this.tw = fftLib().makeTwiddles(FFT_N);
        this.cn = null;
    }
    ChromaMeter.prototype.frame = function (x, off) {
        var w = hann(FFT_N), re = this.re, im = this.im, i, s = 0;
        off = off || 0;
        for (i = 0; i < FFT_N; i++) { var v = x[off + i] || 0; re[i] = v * w[i]; im[i] = 0; s += v * v; }
        var rms = Math.sqrt(s / FFT_N);
        if (rms < MC.LIMITS.SILENT_RMS) return { chroma: null, rms: rms };
        fftLib().fft(re, im, this.tw);
        var top = Math.floor(MC.LIMITS.CHROMA_FMAX * FFT_N / this.sr);
        for (i = 0; i < this.mag.length; i++) this.mag[i] = i <= top ? Math.sqrt(re[i] * re[i] + im[i] * im[i]) : 0;
        var c = root.__chromaStream.frame(this.mag, this.bank);
        var nf = null;
        if (netReady()) {
            if (!this.cn) this.cn = new root.ChordNet.Features(this.sr, fftLib());
            nf = this.cn.frame(x, off);
        }
        return { chroma: c ? Array.prototype.slice.call(c) : null, rms: rms, net: nf };
    };

    function modeOf(a) {
        var c = {}, best = null, bn = 0;
        a.forEach(function (v) { if (v == null) return; c[v] = (c[v] || 0) + 1; if (c[v] > bn) { bn = c[v]; best = v; } });
        return bn >= 2 ? best : null;
    }
    function pool(chromas) {
        var p = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], n = 0;
        chromas.forEach(function (c) { if (!c) return; n++; for (var i = 0; i < 12; i++) p[i] += c[i]; });
        if (!n) return null;
        var mx = Math.max.apply(null, p);
        return mx > 0 ? p.map(function (v) { return v / mx; }) : null;
    }
    function cosine(a, b) {
        var ab = 0, aa = 0, bb = 0;
        for (var i = 0; i < 12; i++) { ab += a[i] * b[i]; aa += a[i] * a[i]; bb += b[i] * b[i]; }
        return aa > 0 && bb > 0 ? ab / Math.sqrt(aa * bb) : 0;
    }
    function template(root, iv) {
        var t = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        iv.forEach(function (i, k) { t[(root + i) % 12] = k === 0 ? 1 : 0.85; });
        return t;
    }
    /* Ожидаемая хрома звучащих нот: каждая струна — с гармониками (у
       струны верхние гармоники сильны: 3-я — квинта, 5-я — терция, поэтому
       голый шаблон трезвучия «видит» не тот аккорд). */
    var HW = [1, 0.8, 0.6, 0.45, 0.35, 0.27, 0.2, 0.15];
    var HSEMI = HW.map(function (_, k) { return Math.round(12 * Math.log2(k + 1)); });
    function expected(midis) {
        var t = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        midis.forEach(function (m) {
            if (m == null) return;
            for (var k = 0; k < HW.length; k++) t[(m + HSEMI[k]) % 12] += HW[k];
        });
        var mx = Math.max.apply(null, t);
        return mx > 0 ? t.map(function (v) { return v / mx; }) : t;
    }
    var FAMILY = { maj: 'maj', '7': 'maj', maj7: 'maj', add9: 'maj', min: 'min', min7: 'min', sus2: 'sus', sus4: 'sus', '5': 'five' };
    function bassOf(midis) {
        for (var i = 0; i < midis.length; i++) if (midis[i] != null) return midis[i] % 12;
        return null;
    }
    /* Соперники: все учебные формы + трезвучия и септаккорды от каждой ноты
       в тесном расположении (то, что может прозвучать, если палец не на том
       ладу). У каждого — своя ожидаемая хрома. */
    var CANDIDATES = null;
    function candidates() {
        if (CANDIDATES) return CANDIDATES;
        CANDIDATES = [];
        Object.keys(MC.SHAPES).forEach(function (k) {
            var c = chord(k);
            if (c && c.strings) CANDIDATES.push({ root: c.root, quality: c.quality, name: c.name, t: expected(c.strings), bass: bassOf(c.strings) });
        });
        for (var r = 0; r < 12; r++) {
            [['maj', [0, 4, 7, 12]], ['min', [0, 3, 7, 12]], ['7', [0, 4, 7, 10]], ['min7', [0, 3, 7, 10]]].forEach(function (q) {
                var base = 45 + ((r - 9 + 12) % 12);   // корень между A2 и G#3
                CANDIDATES.push({ root: r, quality: q[0], name: SHARPS[r] + (q[0] === 'maj' ? '' : q[0] === 'min' ? 'm' : q[0] === 'min7' ? 'm7' : '7'), t: expected(q[1].map(function (i) { return base + i; }).concat([base + q[1][1] + 12])), bass: r });
            });
        }
        return CANDIDATES;
    }

    /*
     * Тот ли аккорд звучит. pooled — хрома окна ~0.9 с, target — chord().
     * Цель — ожидаемая хрома ЕЁ аппликатуры (струны урока с гармониками);
     * соперники — все другие формы и трезвучия (candidates()). Зачёт — цель
     * не хуже лучшего соперника другой «семьи» больше чем на CHORD_FIT_MARGIN
     * и сама похожа на услышанное не меньше CHORD_FIT_MIN. Слабый тон
     * аккорда (его нет в хроме) — отдельно: по нему шаг говорит, какую
     * струну проверить. Числа — docs/why/guitar-course.md.
     */
    function judgeChord(pooled, target, bass) {
        var L = MC.LIMITS;
        if (!pooled || !target) return { ok: false, heard: null, fit: 0 };
        var fam = FAMILY[target.quality] || 'maj';
        var tBass = target.strings ? bassOf(target.strings) : target.root;
        var bonus = function (b) { return bass == null || b == null ? 0 : (b === bass ? L.BASS_BONUS : 0); };
        var tScore = cosine(pooled, expected(target.strings || target.pcs.map(function (pc) { return 48 + pc; }))) + bonus(tBass);
        var best = null, bestOther = null;
        candidates().forEach(function (c) {
            var sc = cosine(pooled, c.t) + bonus(c.bass);
            if (!best || sc > best.s) best = { name: c.name, root: c.root, quality: c.quality, s: sc };
            var same = c.root === target.root && (FAMILY[c.quality] === fam || fam === 'sus' || fam === 'five' || FAMILY[c.quality] === 'five');
            if (!same && (!bestOther || sc > bestOther.s)) bestOther = { name: c.name, root: c.root, quality: c.quality, s: sc };
        });
        var ok = tScore >= L.CHORD_FIT_MIN && tScore >= bestOther.s - L.CHORD_FIT_MARGIN;
        var weak = [];
        target.pcs.forEach(function (pc) { if (pooled[pc] < L.TONE_WEAK) weak.push(pc); });
        var res = { ok: ok, fit: tScore, other: bestOther, heard: best ? best.name : null, weak: weak, bass: bass, judge: 'tpl' };
        if (pooled.net && netReady() && target.strings) {
            var v = root.ChordNet.verify(root.ChordNet.forward(NET.W, pooled.net), target.strings, NET.W.T);
            var wk = [];
            v.weakStrings.forEach(function (i) { var pc = target.strings[i] % 12; if (wk.indexOf(pc) < 0) wk.push(pc); });
            res.ok = v.ok; res.weak = wk; res.judge = 'net'; res.netMargin = v.margin; res.present = v.present;
        }
        tellJudge(res.judge);
        return res;
    }

    /* Бас — нота самой низкой звучащей струны: детектор тюнера по сигналу,
       из которого убран верх (два НЧ-звена ~260 Гц). У аккордов-соседей
       (Em и Cmaj7, A7 и F#m7) хрома почти одна, а бас разный. */
    function BassMeter(sr) {
        var G = root.GuitarTunerCore;
        this.det = G ? new G.Detector({ sampleRate: sr, fft: fftLib(), fmin: 70, fmax: 330 }) : null;
        this.a = 1 - Math.exp(-2 * Math.PI * 260 / sr);
        this.buf = null;
    }
    BassMeter.prototype.read = function (x, off, len) {
        if (!this.det) return null;
        var n = this.det.n;
        len = len || n;
        if (!this.buf || this.buf.length !== n) this.buf = new Float32Array(n);
        var a = this.a, l1 = 0, l2 = 0, start = off + len - n - 512;
        for (var i = Math.max(0, start); i < off + len; i++) {
            l1 += a * ((x[i] || 0) - l1); l2 += a * (l1 - l2);
            var k = i - (off + len - n);
            if (k >= 0) this.buf[k] = l2;
        }
        var r = this.det.analyze(this.buf);
        if (!r.freq || r.clarity < MC.LIMITS.BASS_CLARITY) return null;
        return ((Math.round(freqToMidi(r.freq)) % 12) + 12) % 12;
    };

    /* Аккорд по буферу целиком: окна по 0.9 с с шагом 0.15 с → доля окон с
       зачётом. Для стенда и для «сыграй и держи» в шаге. */
    function chordOverBuffer(pcm, sr, target, from, to) {
        var meter = new ChromaMeter(sr), bm = new BassMeter(sr);
        var hop = Math.round(sr * 0.1), win = Math.round(sr * 0.9 / hop);
        var frames = [], basses = [], nets = [];
        var a = Math.max(0, Math.round((from || 0) * sr)), b = Math.min(pcm.length, Math.round((to || pcm.length / sr) * sr));
        for (var off = a; off + FFT_N <= b; off += hop) { var fr = meter.frame(pcm, off); frames.push(fr.chroma); nets.push(fr.net); basses.push(bm.read(pcm, off, FFT_N)); }
        var verdicts = [];
        for (var i = win; i <= frames.length; i++) verdicts.push(judgeChord(withNet(pool(frames.slice(i - win, i)), nets.slice(i - win, i)), target, modeOf(basses.slice(i - win, i))));
        return verdicts;
    }

    /*
     * Удары по струнам — спектральный поток: сумма РОСТА логарифма спектра по
     * частотам между соседними окнами (1024 отсчёта, шаг 256). Новый удар
     * заново возбуждает гармоники, и поток вспыхивает, даже когда общая
     * громкость поверх звенящего аккорда почти не меняется (энергетический
     * детектор находил на стенде 8 % ударов). Порог — медиана потока за
     * последние ~0.3 с × ONSET_K + ONSET_DELTA, пик — локальный максимум,
     * не чаще раза в ONSET_REFRACTORY_S. Время удара — начало окна пика минус
     * постоянный сдвиг окна (ONSET_BIAS_S, из стенда). Секунды на часах
     * источника.
     */
    function OnsetDetector(sr, opts) {
        opts = opts || {};
        this.sr = sr;
        this.n = 1024;
        this.hop = 256;
        this.buf = new Float32Array(this.n);
        this.fill = 0;
        this.pos = 0;
        this.prev = null;
        this.re = new Float64Array(this.n);
        this.im = new Float64Array(this.n);
        this.tw = fftLib().makeTwiddles(this.n);
        this.flux = [];          // [{v, at}]
        this.lastOnset = -1e9;
        this.k = opts.k || MC.LIMITS.ONSET_K;
        this.delta = opts.delta != null ? opts.delta : MC.LIMITS.ONSET_DELTA;
        this.refr = opts.refractory || MC.LIMITS.ONSET_REFRACTORY_S;
        this.floor = opts.floorMag != null ? opts.floorMag : MC.LIMITS.ONSET_FLOOR_MAG;
    }
    OnsetDetector.prototype._frame = function (at) {
        var w = hann(this.n), re = this.re, im = this.im, n = this.n, i;
        for (i = 0; i < n; i++) { re[i] = this.buf[i] * w[i]; im[i] = 0; }
        fftLib().fft(re, im, this.tw);
        var half = n >> 1, mag = new Float32Array(half), f = 0;
        var lo = Math.floor(70 * n / this.sr), hi = Math.min(half, Math.floor(6000 * n / this.sr));
        /* полоса щелчка метронома (3.1–3.5 кГц) вырезана: атака удара живёт и
           выше 2 кГц, поэтому полосу не обрезаем, а только вынимаем щелчок */
        var c1 = Math.floor(MC.LIMITS.CLICK_BAND[0] * n / this.sr), c2 = Math.ceil(MC.LIMITS.CLICK_BAND[1] * n / this.sr);
        if (!this.peak) this.peak = new Float32Array(half);
        var P = this.peak, mu = MC.LIMITS.ONSET_WHITEN_MU, fl = this.floor, be = 0;
        for (i = 0; i < half; i++) {
            var m = Math.sqrt(re[i] * re[i] + im[i] * im[i]);
            P[i] = Math.max(m, mu * P[i], fl);
            mag[i] = m / P[i];
            if (i >= lo && i < hi && (i < c1 || i > c2)) be += m * m;
        }
        /* громкость полосы удара (70 Гц – 6 кГц без полосы щелчка), в единицах
           среднеквадратичного значения сигнала: щелчок метронома не должен
           открывать ворота громкости кадрам затухающего хвоста */
        var rms = Math.sqrt(2 * be / 0.375) / n;
        if (this.prev) for (i = lo; i < hi; i++) { if (i >= c1 && i <= c2) continue; var d = mag[i] - this.prev[i]; if (d > 0) f += d; }
        this.prev = mag;
        /* уровень комнаты: мгновенно падает к тихому кадру, растёт медленно —
           звенящий аккорд не успевает стать «комнатой» */
        /* старт — с порога тишины, а не с первого кадра: если человек уже
           бьёт по струнам, когда шаг включил микрофон, первый кадр — звенящий
           аккорд, и «комната» навсегда осталась бы громкой (e2e: ни одного
           удара при уровне −18 дБ) */
        if (this.room == null) this.room = MC.LIMITS.ONSET_FLOOR_RMS;
        if (rms < this.room) this.room = Math.max(rms, 1e-6);
        else this.room *= 1.0005;            // ≈ +0.8 дБ/с
        var loud = rms >= MC.LIMITS.ONSET_FLOOR_RMS && rms >= this.room * MC.LIMITS.ONSET_SNR;
        return { v: loud ? f / (hi - lo - (c2 - c1 + 1)) : 0, at: at, rms: rms };
    };
    OnsetDetector.prototype.push = function (x, startPos) {
        if (startPos != null && this.fill === 0) this.pos = startPos;
        var found = [];
        for (var i = 0; i < x.length; i++) {
            this.buf.copyWithin(0, 1);
            this.buf[this.n - 1] = x[i];
            this.fill++;
            if (this.fill >= this.n && (this.fill - this.n) % this.hop === 0) {
                var at = (this.pos + i + 1 - this.n) / this.sr;
                var fr = this._frame(at);
                var F = this.flux;
                F.push(fr);
                if (F.length > 64) F.shift();
                // пик в окне [−2, +2]: решаем о кадре F[len−3]
                if (F.length >= 24) {
                    var c = F.length - 3, cv = F[c].v;
                    var local = cv > 0 && cv >= F[c - 1].v && cv >= F[c - 2].v && cv > F[c + 1].v && cv >= F[c + 2].v;
                    if (local) {
                        var hist = F.slice(Math.max(0, c - 56), c).map(function (q) { return q.v; }).sort(function (p, q) { return p - q; });
                        var med = hist[hist.length >> 1] || 0;
                        /* громкость кадра — не ниже недавнего пика больше чем на
                           ONSET_SPAN_DB: иначе это шум на затухающем хвосте, который
                           выбеливание подняло до уровня удара */
                        var peakR = 0;
                        for (var q = Math.max(0, c - 60); q <= c; q++) if (F[q].rms > peakR) peakR = F[q].rms;
                        var rises = Math.max(F[c].rms, F[c + 1].rms) >= peakR * Math.pow(10, -MC.LIMITS.ONSET_SPAN_DB / 20);
                        if (rises && cv > med * this.k + this.delta && F[c].at - this.lastOnset >= this.refr) {
                            this.lastOnset = F[c].at;
                            found.push(F[c].at + MC.LIMITS.ONSET_BIAS_S);
                        }
                    }
                }
            }
        }
        this.pos += x.length;
        this.fill = Math.min(this.fill, this.n + this.hop * 1000);
        return found;
    };
    function onsetsOverBuffer(pcm, sr) {
        var d = new OnsetDetector(sr);
        var all = [];
        for (var off = 0; off < pcm.length; off += 1024) all = all.concat(d.push(pcm.subarray(off, Math.min(pcm.length, off + 1024))));
        return all;
    }

    /* Ритм: ожидаемые моменты (сек) против услышанных → попадания, лишние,
       средний сдвиг (+ — опаздывает) и разброс. */
    function judgeRhythm(expected, heard, tol) {
        tol = tol || MC.LIMITS.RHYTHM_TOL_S;
        var used = {}, hits = 0, errs = [], marks = [];
        expected.forEach(function (e) {
            var best = -1, bd = 1e9;
            heard.forEach(function (h, j) { var d = Math.abs(h - e); if (!used[j] && d < bd) { bd = d; best = j; } });
            if (best >= 0 && bd <= tol) { used[best] = 1; hits++; errs.push(heard[best] - e); marks.push({ t: e, hit: true, err: heard[best] - e }); }
            else marks.push({ t: e, hit: false, err: null });
        });
        var extras = heard.filter(function (_, j) { return !used[j]; }).length;
        var mean = errs.length ? errs.reduce(function (a, b) { return a + b; }, 0) / errs.length : 0;
        var sd = errs.length > 1 ? Math.sqrt(errs.reduce(function (a, b) { return a + (b - mean) * (b - mean); }, 0) / (errs.length - 1)) : 0;
        var score = expected.length ? Math.max(0, (hits - 0.5 * extras) / expected.length) : 1;
        return { hits: hits, total: expected.length, extras: extras, mean: mean, sd: sd, score: Math.min(1, score), marks: marks };
    }

    MC.analysis = {
        ChromaMeter: ChromaMeter, BassMeter: BassMeter, modeOf: modeOf, pool: pool, cosine: cosine, judgeChord: judgeChord, chordOverBuffer: chordOverBuffer,
        OnsetDetector: OnsetDetector, onsetsOverBuffer: onsetsOverBuffer, judgeRhythm: judgeRhythm, template: template
    };

    /* Пороги. Каждое число — из замера на стенде, см. docs/why/guitar-course.md. */
    MC.LIMITS = {
        SILENT_RMS: 0.0025,       // окно тише — не аккорд, а комната
        CHROMA_FMAX: 2000,        // хрома — ниже щелчка метронома (3.1–3.5 кГц)
        CLICK_BAND: [2800, 3900], // эту полосу детектор ударов не слушает
        CHORD_FIT_MIN: 0.70,      // косинус с ожидаемой хромой аппликатуры
        CHORD_FIT_MARGIN: 0.01,   // цель может уступить лучшему сопернику не больше
        CHORD_HOLD_N: 2,          // окон подряд с зачётом (окна через 0.1 с)
        BASS_BONUS: 0.1,          // совпал бас формы — прибавка к косинусу
        BASS_CLARITY: 0.7,      // бас считается прочитанным с такой чёткостью NSDF          // окон подряд с зачётом (~0.3 с после первой секунды)
        TONE_WEAK: 0.15,          // тон аккорда тише — «не слышно ноты»
        PITCH_OK_CENTS: 40,       // нота засчитана, если ближе
        PITCH_HOLD_MS: 220,       // и держится столько
        ONSET_K: 1.3,
        ONSET_DELTA: 0.03,
        ONSET_FLOOR_RMS: 0.0008,
        ONSET_FLOOR_MAG: 0.02,
        ONSET_WHITEN_MU: 0.997,
        ONSET_SPAN_DB: 30,
        ONSET_SNR: 4,               // удар громче комнаты на 12 дБ
        ONSET_REFRACTORY_S: 0.14,   // восьмые при 120 уд/мин — 250 мс; двойник удара — до ~120 мс
        ONSET_BIAS_S: 0.006,        // окно ловит удар на ~6 мс раньше (медиана стенда)
        RHYTHM_TOL_S: 0.09        // начинающему — ±90 мс
    };

    /* ── живой микрофон ─────────────────────────────────────────────── */

    var workletReady = new WeakMap();
    /*
     * MusicCore.listen(api) → Promise<Listener>. Микрофон без обработки
     * браузера (шумодав и АРУ съедают атаку и тихую струну).
     * Listener: { ctx, sr, onBlock(fn(pcm, frame)), latest(n), timeOf(frame),
     *             level(), stop() } — frame на часах графа ctx.
     */
    MC.listen = function (api, opts) {
        opts = opts || {};
        var constraints = { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } };
        return Promise.all([api.audioCtx(), api.mic(constraints)]).then(function (r) {
            var ctx = r[0], stream = r[1];
            var sr = ctx.sampleRate;
            var src = ctx.createMediaStreamSource(stream);
            var size = Math.round(sr * (opts.seconds || 12));
            var ring = new Float32Array(size), head = 0, total = 0, lastFrame = 0;
            var subs = [], lvl = 0, node = null, sink = ctx.createGain();
            sink.gain.value = 0;
            sink.connect(ctx.destination);
            function take(pcm, frame) {
                for (var i = 0; i < pcm.length; i++) { ring[head] = pcm[i]; head = (head + 1) % size; }
                total += pcm.length;
                lastFrame = frame + pcm.length;
                var s = 0; for (i = 0; i < pcm.length; i++) s += pcm[i] * pcm[i];
                lvl = Math.sqrt(s / pcm.length);
                for (var k = 0; k < subs.length; k++) { try { subs[k](pcm, frame); } catch (e) { if (root.console) console.error(e); } }
            }
            function viaScript() {
                var sp = ctx.createScriptProcessor(1024, 1, 1);
                sp.onaudioprocess = function (e) {
                    var d = new Float32Array(e.inputBuffer.getChannelData(0));
                    take(d, Math.round(e.playbackTime * sr) - d.length);
                };
                src.connect(sp);
                sp.connect(sink);
                node = sp;
            }
            var url = CFG.worklet;
            var p;
            if (ctx.audioWorklet && url && typeof AudioWorkletNode === 'function') {
                if (!workletReady.has(ctx)) workletReady.set(ctx, ctx.audioWorklet.addModule(url));
                p = workletReady.get(ctx).then(function () {
                    var w = new AudioWorkletNode(ctx, 'course-capture');
                    w.port.onmessage = function (e) { if (e.data && e.data.pcm) take(e.data.pcm, e.data.frame); };
                    src.connect(w);
                    w.connect(sink);
                    node = w;
                }, function () { viaScript(); });
            } else { viaScript(); p = Promise.resolve(); }
            return p.then(function () {
                var L = {
                    ctx: ctx, sr: sr, stream: stream,
                    onBlock: function (fn) { subs.push(fn); return function () { subs = subs.filter(function (x) { return x !== fn; }); }; },
                    latest: function (n) {
                        var out = new Float32Array(n);
                        var have = Math.min(n, total);
                        for (var i = 0; i < have; i++) out[n - have + i] = ring[(head - have + i + size * 2) % size];
                        return out;
                    },
                    frameNow: function () { return lastFrame; },
                    timeOf: function (frame) { return frame / sr; },
                    level: function () { return lvl; },
                    stop: function () {
                        subs = [];
                        try { if (node && node.port) node.port.postMessage('stop'); } catch (_) {}
                        try { src.disconnect(); } catch (_) {}
                        try { node && node.disconnect(); } catch (_) {}
                        try { sink.disconnect(); } catch (_) {}
                    }
                };
                api.onCleanup(L.stop);
                return L;
            });
        });
    };

    /* Живая высота тона: onPitch({midi, freq, cents, note, clarity}|null, t). */
    MC.pitchStream = function (listener, onPitch, opts) {
        opts = opts || {};
        var G = root.GuitarTunerCore;
        var det = new G.Detector({ sampleRate: listener.sr, fft: fftLib(), fmin: opts.fmin || 70, fmax: opts.fmax || 1400 });
        var tr = new G.Tracker({ holdMs: opts.holdMs || 350 });
        var n = det.n, k = 0;
        return listener.onBlock(function (pcm, frame) {
            if ((k++ & 1) === 1) return;              // каждые ~43 мс
            var a = det.analyze(listener.latest(n));
            var t = (frame + pcm.length) / listener.sr * 1000;
            var r = tr.push(a, t);
            if (!r || !r.fresh) { onPitch(r && r.held ? null : null, t, a); return; }
            var midi = freqToMidi(r.freq);
            onPitch({ midi: midi, freq: r.freq, cents: Math.round((midi - Math.round(midi)) * 100), note: noteName(midi), clarity: a.clarity }, t, a);
        });
    };

    /* Живая хрома: onChroma({pooled, bass, t, rms, frame}) каждые ~100 мс,
       окно ~0.9 с; frame = {t, c, rms, bass} — для своих окон (смена
       аккорда по тактам). judgeFrames(frames, target) — вердикт по ним. */
    MC.chromaStream = function (listener, onChroma) {
        var meter = new ChromaMeter(listener.sr), bm = new BassMeter(listener.sr);
        var frames = [], acc = 0, hop = Math.round(listener.sr * 0.1);
        ensureNet();
        return listener.onBlock(function (pcm, frame) {
            acc += pcm.length;
            if (acc < hop) return;
            acc = 0;
            var x = listener.latest(FFT_N);
            var r = meter.frame(x, 0);
            var t = (frame + pcm.length) / listener.sr;
            var f = { t: t, c: r.chroma, rms: r.rms, bass: r.chroma ? bm.read(x, 0, FFT_N) : null, net: r.net };
            frames.push(f);
            while (frames.length && frames[0].t < t - 0.9) frames.shift();
            onChroma({ pooled: withNet(pool(frames.map(function (q) { return q.c; })), frames.map(function (q) { return q.net; })), bass: modeOf(frames.map(function (q) { return q.bass; })), t: t, rms: r.rms, frame: f });
        });
    };
    MC.judgeFrames = function (frames, target) {
        return judgeChord(withNet(pool(frames.map(function (q) { return q.c; })), frames.map(function (q) { return q.net; })), target, modeOf(frames.map(function (q) { return q.bass; })));
    };
    /* Веса ChordNet напрямую (стенд, тест). null — снова шаблоны. */
    MC.useChordNet = function (W) { NET.W = W || null; };

    /* Живые удары: onOnset(graphTimeSeconds). */
    MC.onsetStream = function (listener, onOnset) {
        var det = new OnsetDetector(listener.sr);
        return listener.onBlock(function (pcm, frame) {
            det.push(pcm, frame).forEach(function (t) { onOnset(t); });
        });
    };

    /* ── метроном ───────────────────────────────────────────────────── */

    /*
     * MusicCore.metronome(ctx, {bpm, beats, countIn, bars, accent}) — щелчки
     * по часам графа с упреждением 120 мс (setInterval только планирует).
     * start() → t0: время первой доли ПОСЛЕ отсчёта. beatAt(i) — время i-й
     * доли от t0 (отсчёт — отрицательные i). onBeat(fn(i)) — когда долю
     * СЛЫШНО (AudibleClock), для подсветки.
     */
    var CLICK_CURVE = new Float32Array(64), CLICK_CURVE_Q = new Float32Array(64);
    for (var ci = 0; ci < 64; ci++) { CLICK_CURVE[ci] = 0.5 - 0.5 * Math.cos(2 * Math.PI * ci / 63); CLICK_CURVE_Q[ci] = CLICK_CURVE[ci] * 0.35; }
    MC.clickEnvelope = function (k, n) { return 0.5 - 0.5 * Math.cos(2 * Math.PI * k / (n - 1)); };
    MC.metronome = function (ctx, o) {
        o = o || {};
        var bpm = o.bpm || 80, spb = 60 / bpm, beats = o.beats || 4;
        var countIn = o.countIn != null ? o.countIn : beats;
        var total = o.bars ? o.bars * beats : Infinity;
        var t0 = 0, next = 0, iv = null, beatFns = [], shown = -1e9, stopped = false;
        var gain = ctx.createGain();
        gain.gain.value = o.volume != null ? o.volume : 0.5;
        gain.connect(ctx.destination);
        function click(t, strong, quiet) {
            var osc = ctx.createOscillator(), g = ctx.createGain();
            /* Щелчок выше 3 кГц: микрофон урока слышит и его, а хрома и
               детектор ударов смотрят только ниже CHROMA_FMAX — так щелчок
               не становится ни «ударом», ни нотой аккорда. */
            osc.frequency.value = strong ? 3520 : 3136;
            /* Огибающая — окно Ханна 24 мс. Синус с резким фронтом (и даже с
               линейным) даёт утечку спектра ниже 2 кГц, а выбеливание детектора
               поднимает её до уровня удара: на стенде щелчки без гитары
               читались как удары все до одного. У окна Ханна боковые лепестки
               спадают круто — щелчок остаётся около своих 3.1–3.5 кГц. */
            g.gain.setValueAtTime(0, t);
            g.gain.setValueCurveAtTime(quiet ? CLICK_CURVE_Q : CLICK_CURVE, t, 0.024);
            osc.connect(g); g.connect(gain);
            osc.start(t); osc.stop(t + 0.03);
        }
        function lag() { try { return root.AudibleClock ? root.AudibleClock.lagS(ctx) : (ctx.outputLatency || 0) + (ctx.baseLatency || 0); } catch (_) { return 0; } }
        var M = {
            bpm: bpm, spb: spb, beats: beats,
            start: function () {
                t0 = ctx.currentTime + 0.25 + countIn * spb;
                next = -countIn;
                iv = setInterval(tick, 25);
                tick();
                return t0;
            },
            beatAt: function (i) { return t0 + i * spb; },
            onBeat: function (fn) { beatFns.push(fn); },
            stop: function () { stopped = true; if (iv) clearInterval(iv); iv = null; try { gain.disconnect(); } catch (_) {} },
            get t0() { return t0; }
        };
        function tick() {
            if (stopped) return;
            while (next < total && t0 + next * spb < ctx.currentTime + 0.12) {
                var bi = ((next % beats) + beats) % beats;
                click(t0 + next * spb, bi === 0 && o.accent !== false, next < 0 && o.quietCount);
                next++;
            }
            var heard = ctx.currentTime - lag();
            var cur = Math.floor((heard - t0) / spb + 1e-6);
            if (cur !== shown && heard >= t0 - countIn * spb) {
                shown = cur;
                beatFns.forEach(function (f) { try { f(cur); } catch (_) {} });
            }
            if (next >= total && heard > t0 + total * spb + 0.3) M.stop();
        }
        return M;
    };

    /* Задержка «щелчок → микрофон услышал удар» на этом устройстве: из
       калибровки (медиана), иначе — из того, что говорит браузер, ×2 (замер
       recording-latency.js: отчёты браузера дают около половины правды). */
    MC.latency = {
        get: function (api, ctx) {
            var v = api.store.get('mc_lag_s', null);
            if (typeof v === 'number' && isFinite(v)) return { s: v, measured: true };
            var lagS = 0;
            try { lagS = root.AudibleClock ? root.AudibleClock.lagS(ctx) : 0; } catch (_) {}
            return { s: Math.min(0.35, 2 * lagS + 0.02), measured: false };
        },
        set: function (api, s) { api.store.set('mc_lag_s', Math.max(-0.1, Math.min(0.45, s))); }
    };

    /* ── рисунки ────────────────────────────────────────────────────── */

    function chordSvg(name, opts) {
        var c = chord(name);
        if (!c || !c.shape || !root.ChordDiagram) return '';
        return root.ChordDiagram.fretboard({ frets: c.shape.frets, fingers: c.shape.fingers, barre: c.shape.barre }, { width: (opts && opts.width) || 112, height: (opts && opts.height) || 128 });
    }
    MC.chordSvg = chordSvg;

    /* Гриф с точками: notes [{s: 1..6, f, root?, label?}], from — первый лад окна. */
    function fretboardSvg(fig) {
        var notes = fig.notes || [];
        var from = fig.from != null ? fig.from : Math.max(0, Math.min.apply(null, notes.map(function (n) { return n.f; }).concat([99])) - (fig.pad || 0));
        if (from === 99) from = 0;
        var frets = fig.frets || 5;
        var W = 320, H = 150, padL = 34, padR = 12, padT = 18, padB = 22;
        var gw = (W - padL - padR) / frets, gh = (H - padT - padB) / 5;
        var s = '<svg viewBox="0 0 ' + W + ' ' + H + '" xmlns="http://www.w3.org/2000/svg" class="mc-fb">';
        for (var i = 0; i < 6; i++) {
            var y = padT + i * gh;
            s += '<line x1="' + padL + '" y1="' + y + '" x2="' + (W - padR) + '" y2="' + y + '" class="mc-fb-str" stroke-width="' + (0.8 + i * 0.25) + '"/>';
            s += '<text x="' + (padL - 14) + '" y="' + (y + 4) + '" class="mc-fb-lbl" text-anchor="middle">' + (i + 1) + '</text>';
        }
        for (var f = 0; f <= frets; f++) {
            var x = padL + f * gw;
            s += '<line x1="' + x + '" y1="' + padT + '" x2="' + x + '" y2="' + (H - padB) + '" class="' + (f === 0 && from === 0 ? 'mc-fb-nut' : 'mc-fb-fret') + '"/>';
            if (f < frets) s += '<text x="' + (x + gw / 2) + '" y="' + (H - 6) + '" class="mc-fb-lbl" text-anchor="middle">' + (from + f + 1) + '</text>';
        }
        notes.forEach(function (n) {
            var y = padT + (n.s - 1) * gh;
            var cx = n.f === 0 ? padL - 4 : padL + (n.f - from - 0.5) * gw;
            if (n.f !== 0 && (n.f - from < 1 || n.f - from > frets)) return;
            s += '<circle cx="' + cx + '" cy="' + y + '" r="' + (n.f === 0 ? 6 : 9) + '" class="' + (n.root ? 'mc-fb-root' : 'mc-fb-dot') + (n.f === 0 ? ' mc-fb-open' : '') + '"/>';
            if (n.label) s += '<text x="' + cx + '" y="' + (y + 4) + '" class="mc-fb-in" text-anchor="middle">' + String(n.label).replace(/[<>&"]/g, '') + '</text>';
        });
        return s + '</svg>';
    }
    MC.fretboardSvg = fretboardSvg;

    /* Схема боя: «D.DUDUDU» по восьмым (D — вниз, U — вверх, . — пауза). */
    function patternHtml(pattern, counts) {
        var cells = String(pattern).split('');
        var per = cells.length;
        var s = '<div class="mc-pat" style="grid-template-columns:repeat(' + per + ',minmax(0,1fr))">';
        cells.forEach(function (c, i) {
            var cnt = counts ? counts[i % counts.length] : (i % 2 === 0 ? String(i / 2 + 1) : '&');
            s += '<div class="mc-pat-c' + (c === '.' ? ' is-rest' : '') + '" data-i="' + i + '"><b>' + (c === 'D' ? '↓' : c === 'U' ? '↑' : c === 'X' ? '✕' : '·') + '</b><span>' + cnt + '</span></div>';
        });
        return s + '</div>';
    }
    MC.patternHtml = patternHtml;
    MC.countWords = function () {
        var c = mt('count');
        return Array.isArray(MT.count) ? MT.count : ['1', '&', '2', '&', '3', '&', '4', '&'];
    };

    function tabHtml(lines) {
        var pre = document.createElement('pre');
        pre.className = 'mc-tab';
        pre.textContent = (lines || []).join('\n');
        return pre;
    }

    function registerFigures(K) {
        K.figure('chord', function (box, fig) {
            var names = fig.chords || [fig.chord];
            var row = document.createElement('div');
            row.className = 'mc-chords';
            names.forEach(function (n) {
                var cell = document.createElement('div');
                cell.className = 'mc-chord';
                cell.innerHTML = '<b class="mc-chord-name">' + String(chord(n) ? chord(n).name : n).replace(/[<>&]/g, '') + '</b>' + chordSvg(n, fig);
                row.appendChild(cell);
            });
            box.appendChild(row);
        });
        K.figure('fretboard', function (box, fig) { box.innerHTML = fretboardSvg(fig); });
        K.figure('tab', function (box, fig) { box.appendChild(tabHtml(fig.tab)); });
        K.figure('pattern', function (box, fig) { box.innerHTML = patternHtml(fig.pattern, MC.countWords()); });
    }

    /* Синтез курса: аккорды — боем, ноты — щипком. Заменяет простые тоны ядра. */
    function installAudio(K) {
        K.audio.notes = function (notes, o) {
            return K.util.audioCtx().then(function (ctx) { return MC.play(ctx, notes, o); });
        };
    }

    MC.install = function (K) {
        K = K || root.CourseKit;
        if (!K || MC._installed) return;
        MC._installed = true;
        registerFigures(K);
        installAudio(K);
    };

    root.MusicCore = MC;
    if (typeof module !== 'undefined' && module.exports) module.exports = MC;
    if (root.CourseKit) MC.install(root.CourseKit);
})(typeof window !== 'undefined' ? window : globalThis);
